/**
 * JSON-LD structured data builders for schema.org types.
 * All paths are relative — crawlers resolve them against the request host.
 */

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Currency Thoughts LLC",
    url: "https://currencythoughts.net",
    logo: "https://currencythoughts.net/og-seal.png",
    description:
      "Veteran-led, family-focused independent insurance agency building generational wealth infrastructure.",
    foundingDate: "2021",
    sameAs: [] as string[],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Currency Thoughts",
    url: "https://currencythoughts.net",
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    name: "Currency Thoughts LLC",
    url: "https://currencythoughts.net",
    logo: "https://currencythoughts.net/og-seal.png",
    description:
      "Independent insurance agency specializing in life insurance, IUL, annuities, and trust strategy for generational wealth.",
    priceRange: "$$",
    // Address, telephone, and openingHours intentionally omitted until verified.
    // Add when public business address is confirmed.
  };
}

export function articleSchema(args: {
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.headline,
    description: args.description,
    url: args.url,
    ...(args.image ? { image: args.image } : {}),
    ...(args.datePublished ? { datePublished: args.datePublished } : {}),
    ...(args.dateModified ? { dateModified: args.dateModified } : {}),
    author: {
      "@type": "Organization",
      name: args.authorName ?? "Currency Thoughts LLC",
    },
    publisher: {
      "@type": "Organization",
      name: "Currency Thoughts LLC",
      logo: {
        "@type": "ImageObject",
        url: "https://currencythoughts.net/og-seal.png",
      },
    },
  };
}

export function productSchema(args: {
  name: string;
  description: string;
  url: string;
  image?: string;
  price: number; // major-unit dollars
  priceCurrency?: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder";
  sku?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: args.name,
    description: args.description,
    url: args.url,
    ...(args.image ? { image: args.image } : {}),
    ...(args.sku ? { sku: args.sku } : {}),
    brand: {
      "@type": "Brand",
      name: "Currency Thoughts — The Currency Thoughts Store",
    },
    offers: {
      "@type": "Offer",
      price: args.price.toFixed(2),
      priceCurrency: args.priceCurrency ?? "USD",
      availability: `https://schema.org/${args.availability ?? "InStock"}`,
      url: args.url,
    },
  };
}

export function faqSchema(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}